import Auth from '@aws-amplify/auth'
import { unwrapResult } from '@reduxjs/toolkit'
import { Link, makeStyles } from '@material-ui/core'
import { Formik, useFormikContext } from 'formik'
import React, { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import * as Yup from 'yup'

import { useActionRunner } from 'src/modules/notification'
import {
  Button,
  Form,
  FormikCheckbox,
  FormikPasswordField,
  FormikTextField,
  Typography,
  FIELD_REQUIRED,
} from 'src/modules/ui'

import { useAuth } from '../hooks'
import { checkUserAuthState } from '../utils'
import FormActions from './FormActions'
import FormHeader from './FormHeader'

const useStyles = makeStyles(theme => ({
  buyerSignupText: {
    marginBottom: theme.spacing(2),
  },
}))

export const SignUpForm = ({ handleSubmit, isSubmitting }) => {
  const classes = useStyles()
  const { setFieldValue, touched, values } = useFormikContext()
  const {
    email,
    firstName,
    lastName,
    organisation: {
      name: organisationName,
      primaryContactName,
      primaryContactEmail,
      registeredAddress: { name: addressName },
    },
  } = values

  useEffect(() => {
    const name = `${firstName} ${lastName}`
    if (
      firstName &&
      lastName &&
      primaryContactName !== name &&
      !touched?.organisation?.primaryContactName
    ) {
      setFieldValue('organisation.primaryContactName', name)
    }
  }, [firstName, lastName, primaryContactName])

  useEffect(() => {
    if (
      email &&
      primaryContactEmail !== email &&
      !touched?.organisation?.primaryContactEmail
    ) {
      setFieldValue('organisation.primaryContactEmail', email)
    }
  }, [email, primaryContactEmail])

  useEffect(() => {
    if (
      organisationName &&
      organisationName !== addressName &&
      !touched?.organisation?.registeredAddress?.name
    ) {
      setFieldValue('organisation.registeredAddress.name', organisationName)
    }
  }, [organisationName, addressName])

  return (
    <div>
      <FormHeader
        title="Get Started As A Buyer "
        subtitle={
          <>
            <Typography className={classes.buyerSignupText}>
              Contact us so we can set you up with a purchasing environment/s
              tailored to your business needs.{' '}
            </Typography>
            <Button
              component="a"
              color="secondary"
              href="mailto:domini@triedandsupplied.com?subject=Buyer Signup"
              target="_blank"
            >
              Contact Us
            </Button>
          </>
        }
      />
      <FormHeader
        title="Sign Up As A Supplier"
        subtitle="Complete the form below to get started."
      />
      <Form>
        <Form.InputGroup title="Login Details">
          <FormikTextField
            fullWidth
            label="Email"
            margin="normal"
            name="email"
            type="text"
          />
          <FormikPasswordField
            fullWidth
            label="Password"
            margin="normal"
            name="password"
          />
        </Form.InputGroup>
        <Form.InputGroup title="Your Details">
          <FormikTextField
            fullWidth
            label="First name"
            margin="normal"
            name="firstName"
            type="text"
          />
          <FormikTextField
            fullWidth
            label="Last Name"
            margin="normal"
            name="lastName"
            type="text"
          />
          <FormikTextField
            fullWidth
            label="Job title"
            margin="normal"
            name="jobTitle"
            type="text"
          />
        </Form.InputGroup>
        <Form.InputGroup title="Business Info">
          <FormikTextField
            name="organisation.name"
            margin="normal"
            label="Business Name"
            fullWidth
          />
          <FormikTextField
            name="organisation.phoneNumber"
            label="Phone Number"
            margin="normal"
            fullWidth
          />
        </Form.InputGroup>
        <Form.InputGroup title="Primary Contact">
          <FormikTextField
            name="organisation.primaryContactName"
            margin="normal"
            label="Name"
            fullWidth
          />
          <FormikTextField
            name="organisation.primaryContactEmail"
            label="Email"
            margin="normal"
            type="email"
            fullWidth
          />
        </Form.InputGroup>
        <Form.InputGroup title="Ordering">
          <FormikTextField
            name="organisation.orderNotificationEmail"
            label="Order Notification Email"
            margin="normal"
            type="email"
            fullWidth
          />
        </Form.InputGroup>
        <Form.InputGroup title="Registered Address">
          <FormikTextField
            name="organisation.registeredAddress.name"
            margin="normal"
            label="Business Name"
            fullWidth
          />
          <FormikTextField
            name="organisation.registeredAddress.line1"
            margin="normal"
            label="Address Line 1"
            fullWidth
          />
          <FormikTextField
            name="organisation.registeredAddress.line2"
            margin="normal"
            label="Address Line 2 (optional)"
            fullWidth
          />
          <FormikTextField
            name="organisation.registeredAddress.city"
            margin="normal"
            label="City"
            fullWidth
          />
          <FormikTextField
            name="organisation.registeredAddress.county"
            margin="normal"
            label="County"
            fullWidth
          />
          <FormikTextField
            name="organisation.registeredAddress.postcode"
            margin="normal"
            label="Postcode"
            fullWidth
          />
        </Form.InputGroup>
        <FormikCheckbox
          name="acceptTerms"
          label={
            <>
              I accept the{' '}
              <Link
                href="https://triedandsupplied.com/terms-and-conditions"
                target="_blank"
              >
                terms and conditions
              </Link>{' '}
              and{' '}
              <Link
                href="https://triedandsupplied.com/privacy-policy"
                target="_blank"
              >
                privacy policy
              </Link>
              .
            </>
          }
        />

        <FormActions>
          <Button
            color="primary"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            size="large"
            type="submit"
          >
            Sign Up As A Supplier
          </Button>
        </FormActions>
      </Form>
    </div>
  )
}

const SignUp = ({ onStateChange, ...props }) => {
  const { createUser } = useAuth()
  const history = useHistory()
  const [doCreateUser] = useActionRunner(createUser, {
    errorNotification: result => {
      try {
        unwrapResult(result)
      } catch (err) {
        if (err.status === 400) {
          return 'Please correct all errors on the form'
        }
      }
    },
  })

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(FIELD_REQUIRED),
    lastName: Yup.string().required(FIELD_REQUIRED),
    email: Yup.string().email().required(FIELD_REQUIRED),
    jobTitle: Yup.string().required(FIELD_REQUIRED),
    password: Yup.string().required(FIELD_REQUIRED),
    acceptTerms: Yup.bool().oneOf(
      [true],
      'You must agree to the terms and conditions'
    ),
    organisation: Yup.object().shape({
      name: Yup.string().required(FIELD_REQUIRED),
      phoneNumber: Yup.string().required(FIELD_REQUIRED),
      orderNotificationEmail: Yup.string().email().required(FIELD_REQUIRED),
      primaryContactName: Yup.string().required(FIELD_REQUIRED),
      primaryContactEmail: Yup.string().required(FIELD_REQUIRED),
      registeredAddress: Yup.object().shape({
        name: Yup.string().required(FIELD_REQUIRED),
        line1: Yup.string().required(FIELD_REQUIRED),
        line2: Yup.string(),
        city: Yup.string().required(FIELD_REQUIRED),
        county: Yup.string().required(FIELD_REQUIRED),
        postcode: Yup.string().required(FIELD_REQUIRED),
      }),
    }),
  })

  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: '',
    password: '',
    acceptTerms: false,
    organisation: {
      name: '',
      orderNotificationEmail: '',
      phoneNumber: '',
      primaryContactName: '',
      primaryContactEmail: '',
      registeredAddress: {
        name: '',
        line1: '',
        line2: '',
        city: '',
        county: '',
        postcode: '',
      },
    },
  }

  const handleSubmit = async (values, { setErrors }) => {
    const result = await doCreateUser(values)
    try {
      unwrapResult(result)
      const { email, password } = values
      const user = await Auth.signIn(email, password)
      await checkUserAuthState(user, onStateChange)
      history.push('/login')
    } catch (err) {
      if (err.data) {
        setErrors(err.data)
      }
    }
  }
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {formik => <SignUpForm {...formik} {...props} />}
    </Formik>
  )
}

export default SignUp
